import React from 'react';
import { Grid, Typography, Box, Paper, Container, Link } from '@mui/material';
import { styled } from '@mui/system';
import first from '../../../assets/1.svg';
import second from '../../../assets/2.svg';
import third from '../../../assets/3.svg';
import fourth from '../../../assets/4.svg';
import { useTranslation } from 'react-i18next';
import authService from '../../../services/authService';
import { NavLink } from 'react-router-dom';

const StyledPaper = styled(Paper)(({ theme }) => ({
  backgroundColor: '#F5F8FF',
  padding: theme.spacing(3),
  borderRadius: '8px',
  textAlign: 'left',
  boxShadow: 'none',
  minHeight: '180px',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  border: '1px solid #E0E5FF',
  height: '100%'
}));

const FeatureBox = ({ title, isFeature }) => {
  const { t } = useTranslation();

  const isAuthenticated = authService.isAuthenticated();

  const featureItems = [
    {
      title: t('🚀 Instant Market Monitoring'),
      description: t(
        'Never miss a listing again. HomeAI scans Switzerland’s top property platforms in real time and alerts you the moment a new match hits the market—giving you a critical head start in high-demand areas.'
      ),
      iconColor: '#DCE4FF', // Color for icon placeholder
      logo: first,
      width: 60
    },
    {
      title: t('🧠 Deep Property Intelligence'),
      description: t(
        'It’s not just about price or square meters. Our AI reads between the lines—analyzing images and descriptions to assess light quality, taxes, noise levels, views, interior finish, and even energy setup. You get insights no basic filter can offer.'
      ),
      iconColor: '#E0E5FF', // Color for icon placeholder
      logo: second,
      width: 40
    },
    {
      title: t('💬 Smarter Than a Filter: The AI Interview'),
      description: t(
        "No more ticking endless boxes. Our conversational assistant quickly understands your lifestyle, routines, family needs, and must-haves—then finds homes that match what truly matters to you."
      ),
      iconColor: '#DCE4FF', // Color for icon placeholder
      logo: third,
      width: 40
    },
    {
      title: t('🎯 Ultra-Personalized Match Engine'),
      description: t(
        'Powered by over 60 real-world data signals—from tax zones and public transit to childcare access and local demand—HomeAI doesn’t just find properties. It finds the right ones, just for you.'
      ),
      iconColor: '#E0E5FF', // Color for icon placeholder
      logo: fourth,
      width: 60
    }
  ];

  return (
    <Container maxWidth="xl">
      <Box id="features" sx={{ flexGrow: 1, padding: '48px 0 80px', mt: 6 }}>
        <Typography variant="h5" fontWeight={700} fontSize={'35px'} align="center" gutterBottom>
          {title}
        </Typography>

        <Grid
          container
          gap={2}
          sx={{
            marginTop: 4,
            display: 'flex',
            justifyContent: 'center'
          }}
        >
          <Grid
            container
            spacing={3}
            sx={{
              display: {
                xs: 'block', // Display items as blocks on extra-small screens (mobile)
                sm: 'block', // Display items as blocks on small screens (tablets)
                md: 'grid' // Use grid layout on medium and larger screens
              },
              gridAutoRows: '1fr'
            }}
          >
            {featureItems.map((item, index) => {
              const isFirstRow = index < 2;
              return (
                <Grid
                  item
                  key={index}
                  sx={{
                    gridColumn: isFirstRow
                      ? index === 0
                        ? 'span 3'
                        : 'span 1'
                      : index === 2
                        ? 'span 1'
                        : 'span 3',
                    gridRow: isFirstRow ? '1 / 2' : '2 / 3',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    height: {
                      xs: 'auto', // Flexible height on extra-small screens
                      sm: 'auto', // Flexible height on small screens
                      md: '100%' // Full height on medium and larger screens
                    }
                  }}
                >
                  <StyledPaper
                    elevation={0}
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      height: '100%',
                      justifyContent: 'space-between'
                    }}
                  >
                    <NavLink
                      to={isAuthenticated ? '/chat' : '?login'}
                      style={{ textDecoration: 'none' }}
                    >
                      <Box>
                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: 700,
                            color: 'black',
                            fontSize: '24px',
                            mb: 2
                          }}
                        >
                          {item.title}
                        </Typography>
                        <Typography
                          variant="body1"
                          sx={{ color: '#6B7280', fontSize: '16px', mb: 3 }}
                        >
                          {item.description}
                        </Typography>
                      </Box>
                      <Box
                        component="img"
                        src={item.logo}
                        alt={item.title}
                        sx={{
                          width: '100%',
                          height: 'auto',
                          maxHeight: { xs: '200px', md: '250px' },
                          objectFit: 'contain',
                          mt: 'auto'
                        }}
                      />
                    </NavLink>
                  </StyledPaper>
                </Grid>
              );
            })}
          </Grid>
        </Grid>
      </Box>
    </Container>
  );
};

export default FeatureBox;
